import {
  ComponentType,
  type ComponentProps,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { components as tourComponents, TourProvider, useTour } from '@reactour/tour';
import { useMyDataQuery } from '@/generated/graphql';
import { TourAutoOpenCancellationContext } from '@/contexts/tourAutoOpenContext';
import { useCampaignEntry } from '@/contexts/campaignEntryContext';

interface TourStep {
  selector: string;
  content: string;
}

const TourContent = ({ WrappedComponent, tourKey }: { WrappedComponent: ComponentType; tourKey: string }) => {
  const { setIsOpen, setCurrentStep } = useTour();
  const { data, loading } = useMyDataQuery();
  const campaignEntry = useCampaignEntry();
  const timeoutRef = useRef<number | null>(null);
  const autoOpenCancelledRef = useRef(false);
  const cancelAutoOpen = useCallback(() => {
    autoOpenCancelledRef.current = true;
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (loading || campaignEntry.loading || campaignEntry.skipOnboarding) return;

    // Don't auto-start tour while profile or onboarding is incomplete
    const user = data?.myData;
    if (!user) return;
    // Job title is derived from the onboarding objective, so it is not part of
    // profile completeness; onboarding completion implies it is set.
    const profileIncomplete = !user.name?.trim() || !user.phone_number?.trim();
    const onboardingIncomplete = !user.onboarding_objective;
    if (profileIncomplete || onboardingIncomplete) return;

    const hasSeenTour = localStorage.getItem(tourKey);
    if (!hasSeenTour && !autoOpenCancelledRef.current) {
      timeoutRef.current = window.setTimeout(() => {
        timeoutRef.current = null;
        if (autoOpenCancelledRef.current) return;
        setIsOpen(true);
        setCurrentStep(0);
      }, 1000);
    }

    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [setIsOpen, setCurrentStep, tourKey, data, loading, campaignEntry.loading, campaignEntry.skipOnboarding]);

  return (
    <TourAutoOpenCancellationContext.Provider value={cancelAutoOpen}>
      <WrappedComponent />
    </TourAutoOpenCancellationContext.Provider>
  );
};

export const withTour = (WrappedComponent: ComponentType, tourKey: string, steps: TourStep[]) => {
  const ClickableTourContent = (props: ComponentProps<typeof tourComponents.Content>) => {
    const handleContentClick = () => {
      if (props.currentStep >= steps.length - 1) {
        props.setIsOpen?.(false);
        localStorage.setItem(tourKey, 'true');
        return;
      }

      props.setCurrentStep(props.currentStep + 1);
    };

    return (
      <button
        type="button"
        className="w-full cursor-pointer rounded-md text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onClick={handleContentClick}
      >
        <tourComponents.Content {...props} />
      </button>
    );
  };

  return () => {
    const closeTour = useCallback(({ setIsOpen }: { setIsOpen: (isOpen: boolean) => void }) => {
      setIsOpen(false);
      localStorage.setItem(tourKey, 'true');
    }, [tourKey]);

    return (
    <TourProvider
      steps={steps}
      components={{ Content: ClickableTourContent }}
      onClickMask={closeTour}
      onClickClose={closeTour}
      styles={{
        popover: (base) => ({
          ...base,
          backgroundColor: 'hsl(var(--card))',
          color: 'hsl(var(--card-foreground))',
          border: '1px solid hsl(var(--border))',
          borderRadius: '8px',
          boxShadow: '0 10px 30px -10px hsl(var(--primary) / 0.3)',
        }),
        badge: (base) => ({
          ...base,
          backgroundColor: 'hsl(var(--primary))',
          color: 'hsl(var(--primary-foreground))',
        }),
      }}
    >
      <TourContent
        WrappedComponent={WrappedComponent}
        tourKey={tourKey}
      />
    </TourProvider>
    );
  };
};
